import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import generatePicker from 'antd/es/date-picker/generatePicker';
import type { Moment } from 'moment';

interface ReminderLeadIds {
    today: number[];
    overdue: number[];
    upcoming: number[];
}

interface Props {
    followUpFilter: string;
    setFollowUpFilter: (val: string) => void;
    reminderLeadIds: ReminderLeadIds | null;
    isReminderLoading: boolean;
    reminderCustomStart: moment.Moment | null;
    reminderCustomEnd: moment.Moment | null;
    setReminderCustomStart: (v: moment.Moment | null) => void;
    setReminderCustomEnd: (v: moment.Moment | null) => void;
    onOpen: () => void;
}

const MomentDatePicker = generatePicker<Moment>(momentGenerateConfig);
const { RangePicker } = MomentDatePicker;

const ReminderDropdown = ({
    followUpFilter,
    setFollowUpFilter,
    reminderLeadIds,
    isReminderLoading,
    reminderCustomStart,
    reminderCustomEnd,
    setReminderCustomStart,
    setReminderCustomEnd,
    onOpen,
}: Props) => {
    const [open, setOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null); // 👈 ref for portal menu

    // Outside click handler: ignore clicks inside button container OR inside the menu
    useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            const target = e.target as Element;
            if (containerRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return; // 👈 don't close if clicking inside menu
            if (target.closest && target.closest('.ant-picker-dropdown')) return; // 👈 don't close if clicking inside date picker popup
            setOpen(false);
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    const handleToggle = () => {
        const next = !open;
        if (next) {
            if (buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setMenuPosition({
                    top: rect.bottom + window.scrollY + 4,
                    left: rect.left + window.scrollX,
                });
            }
            onOpen();
        }
        setOpen(next);
    };

    const todayCount = reminderLeadIds?.today?.length ?? 0;
    const overdueCount = reminderLeadIds?.overdue?.length ?? 0;
    const upcomingCount = reminderLeadIds?.upcoming?.length ?? 0;

    const getLabel = () => {
        if (followUpFilter === "today") return `Today (${todayCount})`;
        if (followUpFilter === "overdue") return `Overdue (${overdueCount})`;
        if (followUpFilter === "upcoming") return `Upcoming (${upcomingCount})`;
        if (followUpFilter === "custom") {
            if (reminderCustomStart && reminderCustomEnd)
                return `${reminderCustomStart.format("DD MMM")} – ${reminderCustomEnd.format("DD MMM")}`;
            return "Custom Range";
        }
        return "Reminders";
    };

    const badgeStyle = (color: string): React.CSSProperties => ({
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "20px",
        height: "20px",
        padding: "0 5px",
        borderRadius: "10px",
        fontSize: "11px",
        fontWeight: 500,
        background: color,
        color: "#fff",
        marginLeft: "auto",
    });

    const itemStyle = (active: boolean, activeColor: string = "#3b5bdb", activeBg: string = "#f0f4ff"): React.CSSProperties => ({
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 14px",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: active ? 500 : 400,
        background: active ? activeBg : "transparent",
        color: active ? activeColor : "inherit",
        borderRadius: "6px",
        margin: "1px 4px",
        userSelect: "none",
        transition: "all 0.2s",
    });

    const handleSelect = (val: string) => {
        setFollowUpFilter(val);
        if (val !== "custom") setOpen(false);
    };

    const handleCustomRange = (dates: any) => {
        if (dates && dates[0] && dates[1]) {
            setReminderCustomStart(dates[0]);
            setReminderCustomEnd(dates[1]);
            setFollowUpFilter("custom");
        } else {
            setReminderCustomStart(null);
            setReminderCustomEnd(null);
        }
    };

    const menuContent = open && (
        <div
            ref={menuRef} // 👈 attach ref to portal menu
            style={{
                position: "absolute",
                top: menuPosition.top,
                left: menuPosition.left,
                zIndex: 9999,
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: "10px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                minWidth: "220px",
                padding: "6px 0",
            }}
        >
            {isReminderLoading ? (
                <div style={{ padding: "16px 14px", fontSize: "13px", color: "#999", display: "flex", alignItems: "center", gap: "8px" }}>
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    Fetching reminders...
                </div>
            ) : (
                <>
                    <div
                        style={itemStyle(followUpFilter === "all", "#3b5bdb", "#f0f4ff")}
                        onClick={() => {
                            setReminderCustomStart(null);
                            setReminderCustomEnd(null);
                            handleSelect("all");
                        }}
                    >
                        <i className="ti ti-list" style={{ fontSize: "14px", color: followUpFilter === "all" ? "#3b5bdb" : "#888" }} />
                        All Reminders
                    </div>

                    <div style={{ margin: "4px 8px", borderTop: "1px solid #f0f0f0" }} />
                    <div style={{ padding: "2px 18px 4px", fontSize: "11px", color: "#aaa", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                        Follow-up
                    </div>

                    <div style={itemStyle(followUpFilter === "today", "#2f9e44", "#ebfbee")} onClick={() => handleSelect("today")}>
                        <i className="ti ti-calendar-check" style={{ fontSize: "14px", color: followUpFilter === "today" ? "#2f9e44" : (todayCount > 0 ? "#2f9e44" : "#888") }} />
                        Today
                        <span style={badgeStyle(todayCount > 0 ? "#2f9e44" : "#aaa")}>{todayCount}</span>
                    </div>

                    <div style={itemStyle(followUpFilter === "overdue", "#e03131", "#fff5f5")} onClick={() => handleSelect("overdue")}>
                        <i className="ti ti-alert-circle" style={{ fontSize: "14px", color: followUpFilter === "overdue" ? "#e03131" : (overdueCount > 0 ? "#e03131" : "#888") }} />
                        Overdue
                        <span style={badgeStyle(overdueCount > 0 ? "#e03131" : "#aaa")}>{overdueCount}</span>
                    </div>

                    <div style={itemStyle(followUpFilter === "upcoming", "#1971c2", "#e7f5ff")} onClick={() => handleSelect("upcoming")}>
                        <i className="ti ti-clock" style={{ fontSize: "14px", color: followUpFilter === "upcoming" ? "#1971c2" : (upcomingCount > 0 ? "#1971c2" : "#888") }} />
                        Upcoming
                        <span style={badgeStyle(upcomingCount > 0 ? "#1971c2" : "#aaa")}>{upcomingCount}</span>
                    </div>

                    <div style={{ margin: "4px 8px", borderTop: "1px solid #f0f0f0" }} />
                    <div style={{ padding: "2px 18px 4px", fontSize: "11px", color: "#aaa", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                        Custom range
                    </div>

                    <div
                        style={{
                            ...itemStyle(followUpFilter === "custom", "#9c36b5", "#f8f0fc"),
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "6px",
                        }}
                        onClick={(e) => { e.stopPropagation(); handleSelect("custom"); }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                            <i className="ti ti-calendar-event" style={{ fontSize: "14px", color: "#9c36b5" }} />
                            <span>Custom date range</span>
                        </div>
                        <div onClick={(e) => e.stopPropagation()} style={{ width: "100%" }}>
                            <RangePicker
                                size="small"
                                style={{ width: "100%", fontSize: "12px" }}
                                value={reminderCustomStart && reminderCustomEnd ? [reminderCustomStart, reminderCustomEnd] : null}
                                onChange={handleCustomRange}
                                format="DD MMM YYYY"
                                allowClear
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
            <button
                ref={buttonRef}
                onClick={handleToggle}
                style={{
                    height: "38px",
                    padding: "0 14px",
                    borderRadius: "6px",
                    border: "1px solid #d0d7de",
                    background: followUpFilter !== "all" ? "#f0f4ff" : "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: followUpFilter !== "all" ? "#3b5bdb" : "#444",
                    whiteSpace: "nowrap",
                    minWidth: "120px",
                }}
            >
                <i className="ti ti-bell" style={{ fontSize: "14px" }} />
                {isReminderLoading && followUpFilter === "all" ? (
                    <span style={{ color: "#999", fontSize: "12px" }}>Loading...</span>
                ) : (
                    getLabel()
                )}
                <i className={`ti ti-chevron-${open ? "up" : "down"}`} style={{ fontSize: "12px", marginLeft: "auto" }} />
            </button>
            {createPortal(menuContent, document.body)}
        </div>
    );
};

export default ReminderDropdown;